import { AuthService, PermissionsApiService } from '@anecdotes/auth-core';
import { UserFacadeService } from '@anecdotes/identity';
import { MonitoringService } from '@anecdotes/monitoring';
import { Injectable } from '@angular/core';
import { createEffect } from '@ngrx/effects';
import { NEVER, catchError, filter, map, skipUntil, switchMap, take, tap } from 'rxjs';
import { CustomerFacadeService } from '@anecdotes/data-store';

@Injectable()
export class MonitoringEffects {
  constructor(
    private authService: AuthService,
    private customerFacade: CustomerFacadeService,
    private monitoringService: MonitoringService,
    private userFacade: UserFacadeService,
    private permissionsApi: PermissionsApiService
  ) {}

  user$ = createEffect(
    () =>
      this.authService.getUser().pipe(
        filter((userClaims) => !!userClaims),
        switchMap((userClaims) =>
          this.permissionsApi.getPermissions().pipe(
            map((permissionsObject) => ({ userClaims, permissionsObject }))
          ),
        ),
        tap((aggregated) => {
          this.monitoringService.setUser({
            userId: aggregated.userClaims.user_id,
            customerId: aggregated.userClaims.customer_id,
            name: aggregated.userClaims.name,
            email: aggregated.userClaims.email,
            role: aggregated.permissionsObject.roles[0], // For now we support only one role for the user
          });
        }),
      ),
    { dispatch: false },
  );

  customer$ = createEffect(
    () =>
      this.authService.getUser().pipe(
        filter((user) => !!user),
        take(1),
        switchMap(() =>
          this.customerFacade.getCurrentCustomer().pipe(
            filter((customer) => !!customer),
            tap((customer) => {
              this.monitoringService.setGlobalContextProperty('customer', {
                accountType: customer.account_type,
                customerName: customer.customer_name,
              });
            })
          )
        )
      ),
    { dispatch: false }
  );

  authStateChangeErrors$ = createEffect(
    () =>
      this.authService.authStateChanged().pipe(
        catchError((err) => {
          this.monitoringService.addError(err, {});
          return NEVER;
        })
      ),
    { dispatch: false }
  );

  tokenBecomesNull$ = createEffect(
    () =>
      this.authService.getAccessToken().pipe(
        skipUntil(this.authService.getAccessToken().pipe(filter((accessToken) => !!accessToken), take(1))),
        filter((accessToken) => !accessToken),
        tap(() => {
          this.monitoringService.addError(
            new Error('Anomaly detected. Access token become null in the middle of the session.'),
            {}
          );
        })
      ),
    { dispatch: false }
  );

  authStateMonitoring$ = createEffect(
    () =>
      this.authService.getUser().pipe(
        switchMap((userClaims) => {
          if (!userClaims) {
            this.monitoringService.addError(new Error('Anomaly detected. User claims became undefined.'), {});
            return NEVER;
          }
          
          if (!userClaims.user_id) {
            this.monitoringService.addError(new Error('Anomaly detected. User id in user claims is undefined.'), {});
            return NEVER;
          }

          return this.userFacade.getUsersDictionary().pipe(
            tap((usersDictionary) => {
              if (!Object.keys(usersDictionary).length) {
                this.monitoringService.addError(new Error('Anomaly detected. Users are not in the state.'), {});
              } else if (!(userClaims.email in usersDictionary)) {
                this.monitoringService.addError(new Error(`Anomaly detected. User with id ${userClaims.email} was not found in state.`), {});
              }
            }));
        }),
        switchMap(() => NEVER)
      ),
    { dispatch: false }
  );
}
